import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChangeContactGroupingButton,
  ChangeOwnerButton,
  contactColumnDef,
  ContactDetails,
  contactRowClassRules,
  RemoveContactButton,
  RestoreContactButton,
} from 'contacts/components/ContactList';
import { UpsertContactButton } from 'contacts/components/UpsertContact';
import { ContactGroupType } from 'contacts/enums';
import { useGetContacts } from 'contacts/hooks';
import { IContact, IContactGroup } from 'contacts/types';
import { AgGridTable, SlidingPanelContent } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';

interface IProps {
  groupType: ContactGroupType;
  contactGroup: IContactGroup;
}

export const ContactGroupContactList = ({ contactGroup, groupType }: IProps) => {
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);

  const { t } = useTranslation();

  const { records, isLoading, loadingPercentage, currentPage } = useGetContacts({
    filters: {
      groupId: contactGroup.id,
      groupType,
    },
    includeContactGroups: 0,
  });

  const openContactDetails = useCallback((data: IContact) => {
    setSelectedContact(data);
  }, []);

  const columnDefs = useMemo(
    () => contactColumnDef({ isGlobal: false, openContactDetails }),
    [openContactDetails]
  );

  const getLayoutType = useCallback(() => {
    switch (groupType) {
      case ContactGroupType.VENDOR:
        return TableLayoutType.VENDOR_CONTACT_GROUP_CONTACT;
      case ContactGroupType.CUSTOMER:
      default:
        return TableLayoutType.CUSTOMER_CONTACT_GROUP_CONTACT;
    }
  }, [groupType]);

  const actionButtons = useCallback(() => {
    return [<UpsertContactButton contactGroup={contactGroup} key="addContactButton" />];
  }, [contactGroup]);

  const selectionActionButtons = useMemo(() => {
    return [
      <ChangeOwnerButton key="changeOwner" />,
      <ChangeContactGroupingButton key="changeContactGroup" />,
      <RemoveContactButton key="removeContact" />,
      <RestoreContactButton key="restoreContact" />,
    ];
  }, []);

  return (
    <AgGridClientTableProvider
      tableId={'contactGroupContactList'}
      entityType="Contact"
      columnDefs={columnDefs}
      data={records as AgGridDataRow[]}
      rowClassRules={contactRowClassRules}
      isDataLoading={isLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      layoutType={getLayoutType()}
      hasRowSelection
      hasLayoutActions
    >
      <SlidingPanelContent title={t('titleContacts')}>
        <AgGridTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
        />

        <ContactDetails
          isOpen={!!selectedContact}
          onClose={() => setSelectedContact(null)}
          contact={selectedContact}
          contactGroupType={groupType}
          onEditContactSuccess={(contact: IContact) => {
            setSelectedContact(contact);
          }}
        />
      </SlidingPanelContent>
    </AgGridClientTableProvider>
  );
};
